import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AboutModal from '../components/AboutModal';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    speakers: 0,
    clubs: 0,
    topics: 0,
    talks: 0
  });
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    club: '',
    password: ''
  });
  const [clubs, setClubs] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchClubs();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [speakersRes, clubsRes, topicsRes, talksRes] = await Promise.all([
        axios.get(`${API}/speakers`, { headers }),
        axios.get(`${API}/clubs`, { headers }),
        axios.get(`${API}/topics`, { headers }),
        axios.get(`${API}/talks`, { headers })
      ]);

      setStats({
        speakers: speakersRes.data.length,
        clubs: clubsRes.data.length,
        topics: topicsRes.data.length,
        talks: talksRes.data.length
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchClubs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/clubs`, { headers });
      setClubs(response.data);
    } catch (err) {
      console.error('Failed to fetch clubs:', err);
    }
  };

  const handleEditProfile = () => {
    setProfileForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      club: user.club,
      password: ''
    });
    setEditProfileOpen(true);
  };

  const handleProfileSubmit = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Only send non-empty fields
      const updateData = {};
      if (profileForm.first_name !== user.first_name) updateData.first_name = profileForm.first_name;
      if (profileForm.last_name !== user.last_name) updateData.last_name = profileForm.last_name;
      if (profileForm.email !== user.email) updateData.email = profileForm.email;
      if (profileForm.club !== user.club) updateData.club = profileForm.club;
      if (profileForm.password) updateData.password = profileForm.password;
      
      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'info', text: 'No changes to save' });
        setLoading(false);
        return;
      }
      
      const response = await axios.put(`${API}/auth/me`, updateData, { headers });
      
      // Update local storage with new user data
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => {
        window.location.reload(); // Reload to update user state
      }, 1500);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="/logo_6270.png" alt="Rotary District 6270" className="h-16 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Speakers Bureau</h1>
                <p className="text-sm text-gray-500">Welcome, {user.first_name}!</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => setAboutOpen(true)} data-testid="about-button">
                About...
              </Button>
              {user.role === 'Administrator' && (
                <Link to="/admin">
                  <Button variant="outline" data-testid="admin-panel-link">Admin Panel</Button>
                </Link>
              )}
              <Button variant="outline" onClick={onLogout} data-testid="logout-button">Logout</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16 w-full">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h2>
          <p className="text-gray-600">Your Rotary Speakers Bureau overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-shadow" data-testid="speakers-stat-card">
            <CardHeader className="pb-3">
              <CardDescription>Total Speakers</CardDescription>
              <CardTitle className="text-3xl text-[#005DAA]">{stats.speakers}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="card-shadow" data-testid="clubs-stat-card">
            <CardHeader className="pb-3">
              <CardDescription>Total Clubs</CardDescription>
              <CardTitle className="text-3xl text-[#005DAA]">{stats.clubs}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="card-shadow" data-testid="topics-stat-card">
            <CardHeader className="pb-3">
              <CardDescription>Total Topics</CardDescription>
              <CardTitle className="text-3xl text-[#005DAA]">{stats.topics}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="card-shadow" data-testid="talks-stat-card">
            <CardHeader className="pb-3">
              <CardDescription>Total Talks</CardDescription>
              <CardTitle className="text-3xl text-[#005DAA]">{stats.talks}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
          <Card className="card-shadow hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle>Browse Talks</CardTitle>
              <CardDescription>Explore available talks and speakers</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/browse">
                <Button className="w-full bg-[#005DAA] hover:bg-[#004A8F]" data-testid="browse-talks-button">
                  Browse Talks
                </Button>
              </Link>
            </CardContent>
          </Card>

          {user.role === 'Administrator' && (
            <Card className="card-shadow hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Admin Panel</CardTitle>
                <CardDescription>Manage users, speakers, clubs, and topics</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/admin">
                  <Button className="w-full bg-[#F7A81B] hover:bg-[#E09517] text-gray-900" data-testid="admin-panel-button">
                    Go to Admin Panel
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>

        {/* User Info */}
        <Card className="mt-8 card-shadow">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Your Profile</CardTitle>
              <Button onClick={handleEditProfile} variant="outline" data-testid="edit-profile-button">
                Edit Profile
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Username</p>
                <p className="font-medium">{user.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{user.first_name} {user.last_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Club</p>
                <p className="font-medium">{user.club}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <p className="font-medium">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'Administrator' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role}
                  </span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <DialogContent data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle>Edit {user.username} profile</DialogTitle>
            <DialogDescription>Update your profile information</DialogDescription>
          </DialogHeader>

          {message && (
            <Alert className={`${
              message.type === 'error' ? 'bg-red-50 border-red-200' :
              message.type === 'success' ? 'bg-green-50 border-green-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <AlertDescription className={
                message.type === 'error' ? 'text-red-800' :
                message.type === 'success' ? 'text-green-800' :
                'text-blue-800'
              }>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-first-name">First Name</Label>
                <Input
                  id="edit-first-name"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  data-testid="edit-first-name"
                />
              </div>
              <div>
                <Label htmlFor="edit-last-name">Last Name</Label>
                <Input
                  id="edit-last-name"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  data-testid="edit-last-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                data-testid="edit-email"
              />
            </div>

            <div>
              <Label htmlFor="edit-club">Club</Label>
              <Select
                value={profileForm.club}
                onValueChange={(value) => setProfileForm({ ...profileForm, club: value })}
              >
                <SelectTrigger data-testid="edit-club">
                  <SelectValue placeholder="Select your club" />
                </SelectTrigger>
                <SelectContent>
                  {clubs.map((club) => (
                    <SelectItem key={club.id} value={club.name}>
                      {club.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-password">New Password (leave empty to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                value={profileForm.password}
                onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })}
                placeholder="Enter new password"
                data-testid="edit-password"
              />
            </div>

            <Alert className="bg-gray-50 border-gray-200 alert-animate">
              <AlertDescription className="text-gray-700 text-sm">
                Note: Username cannot be changed.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProfileOpen(false)} data-testid="cancel-edit-profile">
              Cancel
            </Button>
            <Button 
              onClick={handleProfileSubmit} 
              disabled={loading}
              className="bg-[#005DAA] hover:bg-[#004A8F]"
              data-testid="save-profile"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* About Modal */}
      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />

      <Footer />
    </div>
  );
}

export default Dashboard;
